import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import {
    fetchOwners,
    getAllDeals,
    getAllLenderDeals,
    getSpecificLenderDeals,
    GetOwnerDataPayload,
    resetDealFilters,
    clearDealsData,
    setCustomFilterDrawer,
} from '../../../../../store/investor/action';
import { DEAL_VIEW_TYPES, FROM_DEBT_OPTIONS_PARAM } from '../../../../../store/investor/constants';
import { USER_TYPE } from '../../../../../utils/constants/user';
import { updateQueryParams } from '../../../../../utils/searchParamUtils';
import {
    checkIfAnyFilterSelected,
    getPlatformUserId,
    getSubdomain,
    isLenderType,
    transformDynamicFilters,
} from '../../../../../utils/utils';
import { INVESTOR_ROUTES, LENDER_ROUTES } from '../../../../../utils/constants/routesConst';
import { getAllTypeOrgList, getDealsOrgList } from './Store/DealsHelper';
import { fetchDataByConfig } from '../../../../../common/ManageDealDrawer/ManageDealDrawerService';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { SORT_DIRECTIONS, SortConfig } from '../../../../../utils/constants/commonConstants';
import { SORT_CLICKED } from '../../../../../utils/constants/mixpanelEvents/investorEvents';
import { selectSelectedColumns } from '../../../../../store/investor/selectors';
import { useIsMobile } from '../../../../../common/_custom/Hooks/useIsMobile';

const LENGTH = 50;

/**
 * Builds the base request params shared by every deals-fetch branch
 * (dealProposerId, pagination, owner, sort). Exported so the on-screen
 * fetch (below) and the "Export to Excel" fetch-all-rows service
 * (dealsExportService.ts) never drift apart on request shape.
 */
export const buildDealsBaseParams = ({
    dealProposerId,
    pageNumber,
    pageSize,
    sortConfig,
    additionalParams = {},
}: {
    dealProposerId: string;
    pageNumber: number;
    pageSize: number;
    sortConfig: SortConfig;
    additionalParams?: Record<string, any>;
}) => ({
    dealProposerId,
    pageNumber,
    pageSize,
    ownerUserId: getPlatformUserId(),
    ...(sortConfig?.direction && { sortConfig }),
    ...additionalParams,
});

/**
 * Builds the `filters` object shared by every deals-fetch branch, applying the
 * exact same appliedFilters -> backend-key remap (via transformDynamicFilters)
 * used on screen. Exported for reuse by the export fetch-all service — do NOT
 * hand-roll this mapping elsewhere (see RECUR-23944).
 */
export const buildDealsFilterPayload = (
    appliedFilters: Record<string, any> | undefined,
    {
        beneficiaryIds,
        receivers,
        transformParams,
    }: {
        beneficiaryIds: string[];
        receivers: string[];
        transformParams?: string[];
    },
) => ({
    dealBeneficiaryIds: beneficiaryIds,
    dealReceivers: receivers,
    products: appliedFilters?.loanTypes || [],
    statuses: appliedFilters?.dealStatus || [],
    ...(appliedFilters?.createdAt && !Array.isArray(appliedFilters.createdAt) && { createdAt: appliedFilters.createdAt }),
    customFields: transformDynamicFilters(
        appliedFilters?.dealProbability && !Array.isArray(appliedFilters.dealProbability)
            ? { ...appliedFilters, 'customFields.debtOptionProbability': appliedFilters.dealProbability }
            : appliedFilters || {},
        transformParams,
    ),
});

/**
 * Resolves the dealReceiverId used to scope a deals fetch to the current lender
 * context — a LENDER/LENDER_TEAM user is scoped to their own org, everyone else to
 * whichever lender is currently selected. Exported so the "Export to Excel"
 * fetch-all-rows service (dealsExportService.ts) reuses the exact same resolution
 * instead of re-deriving it.
 */
export const getDealReceiverId = (userType: string, selectedLenderId: string | null): string => {
    if ([USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM].includes(userType)) {
        return sessionStorage.getItem('user_org_id') || localStorage.getItem('user_org_id') || '';
    }
    return selectedLenderId || '';
};

export const useDealsList = () => {
    const params = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useSearchParams();

    const showDealDrawer = useAppSelector((state) => state.investor.dealDrawer.open);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const fetchingDeals = useAppSelector((state) => state.investor.fetchingDeals);
    const dealsData = useAppSelector((state) => state.investor.deals);
    const user = useAppSelector((state) => state.user);
    const userData = useAppSelector((state) => state.user.userData);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const userType = user.userData.accountType;
    const location = useLocation();
    const isMobile = useIsMobile();

    const [scrollPos, setScrollPos] = useState<number>(0);
    const [orgList, setOrgList] = useState<any[]>([]);
    const [orgListWithDeals, setOrgListWithDeals] = useState<any[]>([]);
    const [filteredOrgList, setFilteredOrgList] = useState<any[]>([]);
    const [displayedOrgList, setDisplayedOrgList] = useState<any[]>([]);
    const [lenders, setLenders] = useState<any[]>([]);

    const [searchText, setSearchText] = useState<string>('');
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [refreshDeals, setRefreshDeals] = useState<boolean>(false);

    // Track when sortConfig is being updated programmatically (not from URL)
    const isProgrammaticSortUpdate = useRef(false);
    // Track previous category to detect tab switches
    const prevCategoryRef = useRef<string | null>(null);
    // Apply fromDebtOptions URL params once on mount so filter drawer shows pre-selected values
    const hasAppliedFromDebtOptionsRef = useRef(false);

    // Initialize sortConfig from URL parameters or defaults
    const pageParam = queryParams.get('page');
    const accessTierParam = queryParams.get('type');
    const selectedLenderId = queryParams.get('lenderId');
    const selectedCategory = queryParams.get('category') || DEAL_VIEW_TYPES.DEAL_VIEW;
    const loanTypeParam = queryParams.get('loanType');
    const fromDebtOptions = queryParams.get(FROM_DEBT_OPTIONS_PARAM) === '1';
    const refetchCompanyDeals = JSON.parse(queryParams.get('refetchCompanyDeals') || 'false');
    const sortFieldParam = queryParams.get('sortField');
    const sortDirectionParam = queryParams.get('sortDirection');
    const sortFieldTypeParam = queryParams.get('sortFieldType');

    // Convert lowercase URL parameter to uppercase for internal state
    const convertDirection = (direction: string | null) => {
        if (!direction) return null;
        return direction.toUpperCase() === 'ASC' ? SORT_DIRECTIONS.ASC : direction.toUpperCase() === 'DESC' ? SORT_DIRECTIONS.DESC : null;
    };
    const isDealOrMandateView = (category: string) => category === DEAL_VIEW_TYPES.DEAL_VIEW || category === DEAL_VIEW_TYPES.MANDATE_VIEW;
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        field: sortFieldParam || 'createdAt',
        direction: convertDirection(sortDirectionParam) || (sortFieldParam ? null : SORT_DIRECTIONS.DESC),
        fieldType: sortFieldTypeParam || '',
    });

    // Column customization state
    const [dealsColumnsConfig, setDealsColumnsConfig] = useState<any>(null);
    const currentColumns = useAppSelector(selectSelectedColumns);

    const appliedFiltersFromState = useAppSelector((state) => {
        // Use shared sourceKey for both DEAL_VIEW and MANDATE_VIEW to preserve filters
        const sourceKey = selectedCategory === 'DEAL_VIEW' || selectedCategory === 'MANDATE_VIEW' ? 'DEALS' : selectedCategory;
        const filters = state.investor.customFilterDrawer.appliedFilters?.[sourceKey];
        return filters;
    });

    // Use ref to track previous filters value for deep comparison
    const prevAppliedFiltersRef = useRef<any>();

    // Only update appliedFilters if content actually changed (not just reference)
    const appliedFilters = useMemo(() => {
        const currentFilters = appliedFiltersFromState || {};

        // Deep compare with previous value
        if (!isEqual(prevAppliedFiltersRef.current, currentFilters)) {
            // Find what specifically changed
            const changes: any = {};
            const prev = prevAppliedFiltersRef.current || {};

            // Check which keys are different
            const allKeys = new Set([...Object.keys(prev), ...Object.keys(currentFilters)]);
            allKeys.forEach((key) => {
                if (!isEqual(prev[key], currentFilters[key])) {
                    changes[key] = {
                        from: prev[key],
                        to: currentFilters[key],
                    };
                }
            });

            prevAppliedFiltersRef.current = currentFilters;
            return currentFilters;
        } else {
            // Return the previous reference to maintain stability
            return prevAppliedFiltersRef.current || currentFilters;
        }
    }, [appliedFiltersFromState]);

    const redirectToPreferences = () => {
        const baseRoute = isLenderType(userType) ? LENDER_ROUTES : INVESTOR_ROUTES;
        navigate(`${baseRoute.APP}/${baseRoute.MANAGE_ELIGIBILITY}`);
    };

    const fetchLenders = async () => {
        try {
            if (userData?.investorOrgId) {
                const response = await fetchDataByConfig(
                    {
                        parentOrgId: userData.investorOrgId,
                    },
                    'GET_ALL_LENDERS',
                    'GET',
                );

                if (response?.data?.lenderMap?.ADMIN) {
                    const res = response.data.lenderMap?.ADMIN;
                    const options = Object.keys(res).map((option: any) => ({
                        label: String(res[option] ?? ''),
                        value: option,
                    }));
                    setLenders(options);
                }
            }
        } catch (error) {
            console.error('Error fetching lenders:', error);
        }
    };

    const CATEGORY_OPTIONS = [
        {
            id: DEAL_VIEW_TYPES.DEAL_VIEW,
            label: 'Deal View',
        },
        {
            id: DEAL_VIEW_TYPES.MANDATE_VIEW,
            label: 'Mandate View',
        },
    ];

    useEffect(() => {
        if (orgList.length > 0 && orgListWithDeals.length > 0) {
            const filteredOrgs = orgList.filter((org) => orgListWithDeals.includes(org.assesseeOrgId));
            setFilteredOrgList(filteredOrgs);
            setDisplayedOrgList(filteredOrgs);
        }
    }, [orgList, orgListWithDeals]);

    useEffect(() => {
        fetchLenders();
    }, [userData?.investorOrgId]);

    useEffect(() => {
        // Don't reset filters and state when switching between DEAL_VIEW and MANDATE_VIEW
        // as these should share the same view state (columns, filters, etc.)
        if (selectedCategory === 'DEAL_VIEW' || selectedCategory === 'MANDATE_VIEW') {
            // Only reset sort config, but preserve filters and columns
            setSortConfig({ field: 'createdAt', direction: null, fieldType: undefined });
            return;
        }

        resetFiltersAndState();
        removeQueryParamsIfPresent(['lenderId', 'orgId', 'sortField', 'sortDirection', 'sortFieldType']);
        // Reset sort config to unsorted when category changes
        setSortConfig({ field: 'createdAt', direction: null, fieldType: undefined });
    }, [selectedCategory]);

    const resetFiltersAndState = () => {
        dispatch(resetDealFilters({ allStatusesSelected: true }));
    };

    const removeQueryParamsIfPresent = (paramsToRemove) => {
        let hasChanges = false;

        paramsToRemove.forEach((param) => {
            if (queryParams.get(param)) {
                queryParams.delete(param);
                hasChanges = true;
            }
        });

        if (hasChanges) {
            updateQueryParams(queryParams, setQueryParams, {}, location);
        }
    };

    useEffect(() => {
        const currentPageInParams = queryParams.get('page');
        const currentPageFromDeals = dealsData?.pageNumber?.toString();

        if (dealsData?.pageNumber && currentPageFromDeals !== currentPageInParams) {
            updateQueryParams(
                queryParams,
                setQueryParams,
                {
                    page: currentPageFromDeals,
                    ...(!accessTierParam && { type: 'deals' }),
                },
                location,
            );
        } else if (!accessTierParam && queryParams.get('type') !== 'deals') {
            updateQueryParams(
                queryParams,
                setQueryParams,
                {
                    type: 'deals',
                },
                location,
            );
        }
    }, [dealsData?.pageNumber, accessTierParam]);

    // Need to persist lenderId in case needInfo drawer is opened in case of refresh
    //for the email modal
    const setLenderIdFromDealId = useCallback(() => {
        const dealIdFromParams = queryParams.get('dealId');
        if (
            !queryParams.has('needInfo') ||
            !dealIdFromParams ||
            !dealsData?.data ||
            dealsData.data.length === 0 ||
            queryParams.has('lenderId')
        )
            return;

        try {
            if (selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW) {
                const parentDeal = dealsData.data.find(
                    (deal) => deal?.investorDeals?.some((investorDeal) => investorDeal?.dealId === dealIdFromParams),
                );

                if (parentDeal?.investorDeals) {
                    const matchingInvestorDeal = parentDeal.investorDeals.find((investorDeal) => investorDeal?.dealId === dealIdFromParams);

                    if (matchingInvestorDeal?.dealReceiverId) {
                        queryParams.set('lenderId', matchingInvestorDeal.dealReceiverId);
                        setQueryParams(queryParams);
                    }
                }
            } else {
                const investorDeal = dealsData.data.find((deal) => deal?.dealId === dealIdFromParams);
                if (investorDeal?.dealId) {
                    const dealReceiverId = (investorDeal as any)?.dealReceiverId;
                    if (dealReceiverId && typeof dealReceiverId === 'string') {
                        queryParams.set('lenderId', dealReceiverId);
                        setQueryParams(queryParams);
                    }
                }
            }
        } catch (error) {
            console.error('Error in setLenderIdFromDealId:', error);
        }
    }, [queryParams, dealsData?.data, selectedCategory, setQueryParams]);

    useEffect(() => {
        setLenderIdFromDealId();
    }, [queryParams, dealsData?.data, selectedCategory]);

    useEffect(() => {
        if (queryParams.get('lenderId') && !queryParams.has('needInfo')) {
            queryParams.delete('lenderId');
            updateQueryParams(queryParams, setQueryParams, {}, location);
        }
        if (queryParams.get('orgId')) {
            queryParams.delete('orgId');
            updateQueryParams(queryParams, setQueryParams, {}, location);
        }
    }, []);

    useEffect(() => {
        if (!platformData) return;

        // Always dispatch on dependency change, even mid-fetch — the deal-fetch actions
        // (GET_ALL_LENDER_DEALS / GET_ALL_DEALS / GET_SPECIFIC_LENDER_DEALS) are takeLatest sagas,
        // so a newer dispatch supersedes the in-flight one. Do NOT reintroduce a fetchingDeals guard:
        // it silently dropped the filtered refetch after saved-view restore (RCPI-10665).

        // Clear deals data only when switching between DEAL_VIEW and MANDATE_VIEW
        // This prevents showing incorrect data when switching tabs
        const prevCategory = prevCategoryRef.current;
        const isCategorySwitch =
            prevCategory !== null &&
            prevCategory !== selectedCategory &&
            isDealOrMandateView(prevCategory) &&
            isDealOrMandateView(selectedCategory);

        if (isCategorySwitch) {
            dispatch(clearDealsData());
        }

        prevCategoryRef.current = selectedCategory;

        // Helper to build base deal request parameters
        const buildBaseParams = (additionalParams = {}) =>
            buildDealsBaseParams({
                dealProposerId: platformData.investorOrgId,
                pageNumber: pageParam ? parseInt(pageParam) : 1,
                pageSize: 50,
                sortConfig,
                additionalParams,
            });

        // Helper to build common filter structure
        const buildFilters = (args: { beneficiaryIds: string[]; receivers: string[]; transformParams?: string[] }) =>
            buildDealsFilterPayload(appliedFilters, args);

        // Handle COMPANY user type
        if (userType === USER_TYPE.COMPANY) {
            const assesseeId = userData?.assesseeOrgId;
            if (assesseeId) {
                const baseParams = buildBaseParams({ dealBeneficiaryId: assesseeId });

                if (selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) {
                    dispatch(
                        getAllLenderDeals({
                            ...baseParams,
                            filters: buildFilters({
                                beneficiaryIds: [assesseeId],
                                receivers: appliedFilters?.lenderName || [],
                            }),
                        }),
                    );
                } else if (selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW) {
                    dispatch(
                        getAllDeals({
                            ...baseParams,
                            filters: buildFilters({
                                beneficiaryIds: [assesseeId],
                                receivers: selectedLenderId ? [selectedLenderId] : [],
                                transformParams: ['customFields.', 'owners.'],
                            }),
                        }),
                    );
                }
            }
        }
        // Handle LENDER user types or DEAL_VIEW for non-company users
        else if (userType === USER_TYPE.LENDER || userType === USER_TYPE.LENDER_TEAM || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) {
            const isLenderUser = [USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM].includes(userType);
            const actionToDispatch = isLenderUser ? getSpecificLenderDeals : getAllLenderDeals;
            const orgIdForFilter = selectedOrgId || params.id ? [selectedOrgId || (params.id as string)] : [];
            const dealReceiverId = getDealReceiverId(userType, selectedLenderId);

            dispatch(
                actionToDispatch({
                    ...buildBaseParams(),
                    filters: buildFilters({
                        beneficiaryIds: orgIdForFilter,
                        receivers: [...(appliedFilters?.lenderName || []), ...(dealReceiverId ? [dealReceiverId] : [])],
                    }),
                }),
            );
        }
        // Handle MANDATE_VIEW for non-company, non-lender users
        else if (selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW) {
            const orgIdForFilter = selectedOrgId || params.id ? [selectedOrgId || (params.id as string)] : [];

            dispatch(
                getAllDeals({
                    ...buildBaseParams(),
                    filters: buildFilters({
                        beneficiaryIds: orgIdForFilter,
                        receivers: selectedLenderId ? [selectedLenderId] : [],
                        transformParams: ['customFields.', 'owners.'],
                    }),
                }),
            );
        }

        // Cleanup: Reset flags and remove query params after dispatching actions
        if (refreshDeals) {
            setRefreshDeals(false);
        }
        if (queryParams.get('dealUpdated')) {
            queryParams.delete('dealUpdated');
            updateQueryParams(queryParams, setQueryParams, {}, location);
        }
        if (queryParams.get('editDealUpdated')) {
            queryParams.delete('editDealUpdated');
            updateQueryParams(queryParams, setQueryParams, {}, location);
        }
    }, [
        platformData,
        pageParam,
        selectedCategory,
        userType,
        userData,
        selectedOrgId,
        refreshDeals,
        selectedLenderId,
        sortConfig,
        queryParams.get('dealUpdated'),
        queryParams.get('editDealUpdated'),
        appliedFilters,
    ]);

    useEffect(() => {
        if (!platformData?.investorOrgId) return;

        getAllTypeOrgList(platformData.investorOrgId, {
            onSuccess: (data) => {
                setOrgListWithDeals(data);
            },
            onError: () => {
                setOrgListWithDeals([]);
            },
        });
    }, [platformData]);

    const getOrganizationList = useCallback(() => {
        getDealsOrgList(
            { pnoId: investorOrgId },
            {
                onSuccess: (res) => {
                    setOrgList(res);
                },
            },
        );
    }, [investorOrgId]);

    const fetchOwnerDetail = useCallback(() => {
        let payload: GetOwnerDataPayload = {
            investorOrgId: userData.investorOrgId,
        };
        dispatch(fetchOwners(payload));
    }, [userData.investorOrgId]);

    useEffect(() => {
        getOrganizationList();
        fetchOwnerDetail();
    }, [getOrganizationList, fetchOwnerDetail]);

    useEffect(() => {
        if (refetchCompanyDeals) {
            setRefreshDeals(true);
            queryParams.delete('refetchCompanyDeals');
            setQueryParams(queryParams);
        }
    }, [refetchCompanyDeals]);

    /**
     * Reads debt options URL params (loanType or lenderId) on mount, applies the corresponding
     * filter to the deals filter drawer, runs only once via a ref guard, and cleans up URL params
     * (fromDebtOptions, loanType, lenderId) afterward via setQueryParams.
     */
    const applyFiltersFromDebtOptionsUrlParams = useCallback(() => {
        if (hasAppliedFromDebtOptionsRef.current) return;
        if (!fromDebtOptions) return;
        if (!loanTypeParam && !selectedLenderId) return;

        hasAppliedFromDebtOptionsRef.current = true;
        dispatch(
            setCustomFilterDrawer({
                open: false,
                appliedFilters: {
                    DEALS: loanTypeParam ? { loanTypes: [loanTypeParam] } : { lenderName: [selectedLenderId!] },
                },
            }),
        );

        queryParams.delete(FROM_DEBT_OPTIONS_PARAM);
        queryParams.delete('loanType');
        queryParams.delete('lenderId');
        setQueryParams(queryParams);
    }, [fromDebtOptions, loanTypeParam, selectedLenderId, dispatch, queryParams, setQueryParams]);

    // Apply filters from debt options URL params once on mount (category already from URL; loanType → Debt Products, lenderId → Lenders)
    useEffect(() => {
        applyFiltersFromDebtOptionsUrlParams();
    }, [applyFiltersFromDebtOptionsUrlParams]);

    const changePageNumber = useCallback(
        (newPageNumber: number) => {
            const newParams = new URLSearchParams(queryParams.toString());
            newParams.set('page', newPageNumber.toString());
            setQueryParams(newParams);
        },
        [queryParams, setQueryParams],
    );

    /** Clears the accumulated mobile deal list. Returns false on desktop, where it is a no-op. */
    const clearMobileDealList = useCallback((): boolean => {
        if (!isMobile) return false;
        dispatch(clearDealsData());
        return true;
    }, [isMobile, dispatch]);

    /**
     * Clears the mobile list and rewinds to page 1. Returns whether it acted.
     * Writes the URL itself, so a caller that also calls setQueryParams must carry page=1 into
     * those params — the later write wins.
     */
    const resetMobileDealList = useCallback((): boolean => {
        if (!clearMobileDealList()) return false;
        changePageNumber(1);
        return true;
    }, [clearMobileDealList, changePageNumber]);

    /**
     * Same reset, skipped when the incoming filters match the active ones. Re-applying an identical
     * filter set triggers no refetch, so clearing would leave an empty list with nothing to refill.
     */
    const resetMobileDealListIfFiltersChanged = useCallback(
        (nextFilters: Record<string, any>): boolean => {
            if (isEqual(nextFilters ?? {}, appliedFilters ?? {})) return false;
            return resetMobileDealList();
        },
        [appliedFilters, resetMobileDealList],
    );

    const getTotalCount = () => {
        // check if filter applied
        if (checkIfAnyFilterSelected(appliedFilters)) {
            return dealsData?.dealCount?.filteredDeals;
        }
        return dealsData?.dealCount?.totalDeals;
    };

    const handleSearch = (text: string) => {
        setSearchText(text);
        if (text === '') {
            // page=1 must be set here too: this write lands after resetMobileDealList's own.
            const didReset = resetMobileDealList();
            setSelectedOrgId(null);
            queryParams.delete('lenderId');
            if (didReset) queryParams.set('page', '1');
            setQueryParams(queryParams);
            setDisplayedOrgList(filteredOrgList);
        } else {
            const searchLower = text.toLowerCase();
            const orgSearchResults = filteredOrgList.filter(
                (org) =>
                    org.orgName?.toLowerCase().includes(searchLower) ||
                    org.orgDisplayName?.toLowerCase().includes(searchLower) ||
                    org.websiteUrl?.toLowerCase().includes(searchLower),
            );

            const lenderSearchResults = lenders
                .filter((lender) => lender.label.toLowerCase().includes(searchLower))
                .map((lender) => ({
                    assesseeOrgId: lender.value,
                    orgName: lender.label,
                }));

            const combinedResults = [...orgSearchResults, ...lenderSearchResults];

            setDisplayedOrgList(combinedResults);
        }
    };

    const handleOrgSelect = (searchedId: string) => {
        resetMobileDealList();
        const isLenderId = lenders.some((lender) => lender.value === searchedId);
        if (isLenderId) {
            const selectedLender = lenders.find((lender) => lender.value === searchedId);
            queryParams.set('lenderId', selectedLender?.value || '');
            queryParams.set('page', '1');
            setQueryParams(queryParams);
            setSelectedOrgId(null);
            setSearchText('');
        } else {
            setSelectedOrgId(searchedId);
            setSearchText('');
            queryParams.set('page', '1');
            queryParams.delete('lenderId');
            setQueryParams(queryParams);
        }
    };

    const changeSelectedTab = useCallback(
        (category: string) => {
            Mixpanel.track('Deal Page', {
                userName: userData?.userName,
                PNOName: getSubdomain(),
                view: category,
            });
            // Clear first so the remounted list shimmers instead of showing the old category's cards.
            clearMobileDealList();
            updateQueryParams(queryParams, setQueryParams, { category: category, page: '1' }, location);
        },
        [clearMobileDealList, queryParams, setQueryParams],
    );

    /**
     * Handles sorting when a column header is clicked.
     *
     * Features:
     * - Implements three-state cycle: unsorted → ASC → DESC → unsorted
     * - Persists sort state in URL parameters
     * - Tracks user interaction with Mixpanel analytics
     * - Converts between internal uppercase and URL lowercase formats
     *
     * @param field - The column field name to sort by
     * @param fieldType - The type of field for sorting (optional)
     */
    const handleSort = useCallback(
        (field: string, fieldType?: string) => {
            let newDirection: (typeof SORT_DIRECTIONS)[keyof typeof SORT_DIRECTIONS] | null;
            let newSortConfig: SortConfig;

            // Three-state cycle: unsorted → ASC → DESC → unsorted
            if (sortConfig.field !== field || sortConfig.direction === null) {
                // If different field or currently unsorted, start with ascending
                newDirection = SORT_DIRECTIONS.ASC;
                newSortConfig = { field, direction: newDirection, fieldType };
            } else if (sortConfig.direction === SORT_DIRECTIONS.ASC) {
                // If currently ascending, switch to descending
                newDirection = SORT_DIRECTIONS.DESC;
                newSortConfig = { field, direction: newDirection, fieldType };
            } else {
                // If currently descending, switch to unsorted but keep the field
                newDirection = null;
                newSortConfig = { field, direction: null, fieldType };
            }

            // Track sort click event
            Mixpanel.track(SORT_CLICKED, {
                currentUrl: window.location.href,
                referral_domain: document.referrer ? new URL(document.referrer).hostname : '',
                user_email: userData?.primaryEmail || '',
                source: selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW ? 'mandate' : 'deals',
                column_name: field,
                sort_order: newDirection ? newDirection.toLowerCase() : 'none',
            });

            // Set flag to indicate programmatic update
            isProgrammaticSortUpdate.current = true;
            setSortConfig(newSortConfig);

            // Update URL parameters to persist sorting (convert to lowercase for URL)
            if (newDirection === null) {
                // Remove sort params when unsorted
                queryParams.delete('sortField');
                queryParams.delete('sortDirection');
                queryParams.delete('sortFieldType');
                setQueryParams(queryParams);
            } else {
                updateQueryParams(
                    queryParams,
                    setQueryParams,
                    {
                        sortField: field,
                        sortDirection: newDirection.toLowerCase(),
                        sortFieldType: fieldType,
                    },
                    location,
                );
            }
        },
        [sortConfig, queryParams, setQueryParams, location, userData, selectedCategory],
    );

    // Sync sortConfig with URL parameters (for browser navigation)
    useEffect(() => {
        // Skip URL sync if this was a programmatic update from handleSort
        if (isProgrammaticSortUpdate.current) {
            isProgrammaticSortUpdate.current = false;
            return;
        }

        const urlSortField = queryParams.get('sortField');
        const urlSortDirection = queryParams.get('sortDirection');
        const urlSortFieldType = queryParams.get('sortFieldType');
        const convertedDirection = convertDirection(urlSortDirection);

        // Only sync if there's a meaningful difference to prevent redundant setSortConfig calls
        if (
            urlSortField &&
            convertedDirection &&
            (sortConfig.field !== urlSortField || sortConfig.direction !== convertedDirection || sortConfig.fieldType !== urlSortFieldType)
        ) {
            // Only update if URL and current config are actually different
            if (sortConfig.field !== urlSortField || sortConfig.direction !== convertedDirection) {
                setSortConfig({
                    field: urlSortField,
                    direction: convertedDirection,
                    fieldType: urlSortFieldType || undefined,
                });
            }
        } else if (!urlSortField && !urlSortDirection) {
            // Only reset if current config is not already in unsorted state
            if (sortConfig.direction !== null) {
                setSortConfig({
                    field: 'createdAt',
                    direction: null,
                    fieldType: undefined,
                });
            }
        }
    }, [queryParams.get('sortField'), queryParams.get('sortDirection'), queryParams.get('sortFieldType')]);

    useEffect(() => {
        if (selectedOrgId) {
            queryParams.set('orgId', selectedOrgId);
        } else {
            queryParams.delete('orgId');
        }
        setQueryParams(queryParams);
    }, [selectedOrgId]);

    useEffect(() => {
        const orgIdFromUrl = queryParams.get('orgId');
        if (orgIdFromUrl !== selectedOrgId) {
            setSelectedOrgId(orgIdFromUrl);
        }
    }, [queryParams]);

    return {
        // States
        showDealDrawer,
        dealDrawerState,
        fetchingDeals,
        dealsData,
        userType,
        scrollPos,
        displayedOrgList,
        selectedCategory,
        accessTierParam,
        LENGTH,
        CATEGORY_OPTIONS,
        showFilters,
        selectedOrgId,
        selectedLenderId,
        sortConfig,
        // Column customization states
        currentColumns,
        dealsColumnsConfig,
        setDealsColumnsConfig,
        // Methods
        setScrollPos,
        getTotalCount,
        changePageNumber,
        clearMobileDealList,
        resetMobileDealList,
        resetMobileDealListIfFiltersChanged,
        handleSearch,
        handleOrgSelect,
        redirectToPreferences,
        setRefreshDeals,
        queryParams,
        changeSelectedTab,
        handleSort,
        appliedFilters,
    };
};
